import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { AiFillStar } from 'react-icons/ai';
import * as apiClient from '../api-client';
import { useSearchContext } from '../contexts/SearchContext';
import GuestInfoForm from '../forms/GuestInfoForm/GuestInfoForm';
import SearchSummaryBar from '../components/SearchSummaryBar';
import HotelDetails from '../components/HotelDetails';
import FAQsComponent from '../components/FAQsComponent';
// import WeatherComponent from '../components/WeatherComponent';
import HotelImageGallery from '../utils/HotelImageGallery';
import Breadcrumbs from '../components/BreadcrumbsComponent';
import SimilarHotelsList from '../components/HotelCard/SimilarHotelsList';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import RoomsAndPrice from '../components/RoomsAndPriceComponent';
//import { AvailabilityResponse, Room, Rate } from '../../../backend/src/models/hotelAvailabilty'; // Adjust the path accordingly
import TripAdvisorRating from '../components/TripAdvisor/TripAdvisorComponent';
import { useHotelContext } from '../contexts/HotelContext';
import PageLoaderComponent from '../components/Common/PageLoader';
import { PropertyAvailabilityResponse, PropertyRoom, Deal } from '../../../backend/src/models/getPropertyAvailability';
import { PropertyDetails } from '../../../backend/src/models/getPropertyDetails';
import { fetchPropertyDetails } from '../api-client';
import ContactDetailCard from '../components/ContactDetailCard';
import GoogleMapComponent from '../components/GoogleMapComponent';
import WhyThisResort from '../components/WhyThisResort';
import { hasWhyThisResort } from '../components/WhyThisResort';
import BlogSliderHome from '../components/Blogs/BlogSliderHome';
import { Link } from 'react-router-dom';
import { getUsdToGbpExchangeRate } from '../api-client';

interface Image {
  url: string;
  alt: string;
}

interface DetailProps {
  slug: string;
}

const Detail: React.FC<DetailProps> = ({ slug }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const searchSummaryBarRef = useRef<{ triggerEditSearchClick: () => void }>(null);

  const handleChangeDatesClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (searchSummaryBarRef.current) {
      searchSummaryBarRef.current.triggerEditSearchClick();
    }
  };

  const targetRef = useRef<HTMLDivElement | null>(null);
  const { checkIn, checkOut, adultCount, childCount, childAges, vacationType, nights } = useSearchContext();
  //const [availabilityData, setAvailabilityData] = useState<AvailabilityResponse | null>(null);
  const [featuredImage, setFeaturedImage] = useState<Image | null>(null);
  const { saveHotelValues, saveRoomAndRate, roomCode, roomName, roomType, roomPrice, hotelId, hotelName, grossPrice, discountPrice } = useHotelContext();
  //const [responseToken, setResponseToken] = useState('');
  const [availablePropertyData, setAvailablePropertyData] = useState<PropertyAvailabilityResponse | null>(null);
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails | null>(null);
  const [showMap, setShowMap] = React.useState(false);
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [rate, setRate] = useState<number | null>(null);

  const handleToggleShowAllRooms = () => {
    setShowAllRooms(!showAllRooms);
  };

  const [showLoader, setShowLoader] = useState(true);
  const delay = 5000;

  const toggleMap = () => {
    setShowMap(!showMap);
  };

  useEffect(() => {
    if (showMap) {
      document.documentElement.classList.add('popup-open'); // Add to <html>
      document.body.classList.add('popup-open'); // Add to <body>
    } else {
      document.documentElement.classList.remove('popup-open'); // Remove from <html>
      document.body.classList.remove('popup-open'); // Remove from <body>
    }
    return () => {
      document.documentElement.classList.remove('popup-open');
      document.body.classList.remove('popup-open');
    };
  }, [showMap]);

  const fetchRate = async () => {
    try {
      const exchangeRate = await getUsdToGbpExchangeRate();
      setRate(exchangeRate);
    } catch (err: any) {
      console.error('Failed to fetch exchange rate:', err);
    }
  };

  useEffect(() => {
    fetchRate();
  }, []);
  // const CheckInDate = checkIn.toISOString().split('T')[0];

  const formatDate = (date: any) => {
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11 ? 'st' :
        day % 10 === 2 && day !== 12 ? 'nd' :
          day % 10 === 3 && day !== 13 ? 'rd' : 'th';

    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();

    return `${day}${suffix} ${month} ${year}`;
  };

  const CheckIn = formatDate(new Date(checkIn));
  const CheckOut = formatDate(new Date(checkOut));

  const { data: hotel, isSuccess, isLoading } = useQuery(
    ['fetchHotelBySlug', slug],
    () => apiClient.fetchHotelBySlug(slug),
    { enabled: !!slug }
  );

  const { data: hotels } = useQuery(
    'fetchHotels',
    apiClient.fetchHotels,
    {
      enabled: true, // Always fetch hotels when the component mounts
    }
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    if (isSuccess && hotel?.imageUrls?.length > 0) {
      setFeaturedImage({ url: hotel.imageUrls[0], alt: 'Featured Image' });
    }

    const getResponseTokenForSearch = async () => {
      try {
        const tokenRequest = {
          source: {
            userEmail: 'ajanth@haloflights.co.uk',
            requestRef: '1000987654321234567890'
          },
          requestCriteria: {
            region: 'CBD',
            destination: {
              destinationCode: hotel!.destinationCode,
              locationCode: 'ANY',
            },
            hotelCodes: [hotel!.destId],
            stayPeriod: {
              checkInDate: checkIn.toISOString().split('T')[0],
              checkOutDate: checkOut.toISOString().split('T')[0],
            },
            nationalityCode: 'GB',
            roomRequests: [
              {
                adults: adultCount,
                children: childCount,
                childAges: childAges.map((age: any) => parseInt(age)),
              },
            ],
            mealTypeCode: '',
            vacationTypeCode: vacationType,
            preferences: {
              availability: 'all',
            },
          }
        };

        const data = await apiClient.getResponseToken(tokenRequest);
        const propertyRoomDetails = await apiClient.fetchPropertyRoomDetails(data.responseToken);
        // console.log('Property Room Details :', propertyRoomDetails);
        setAvailablePropertyData(propertyRoomDetails);

        if (propertyRoomDetails.result.length === 0) {
          saveHotelValues(
            '',
            'Not available for Selected Dates',
            hotel?.name ?? '',
            ' - ',
            ' - ',
            '-',
            '-',
            '-',
            '-'
          );
          saveRoomAndRate(
            null,
            null
          );
        } else if (roomCode === "" || hotelId != propertyRoomDetails?.result[0].hotelCode || roomPrice === roomPrice) {
          saveHotelValues(
            propertyRoomDetails?.result[0].groups[0].rooms[0].code ?? '',
            propertyRoomDetails?.result[0].groups[0].rooms[0].name ?? '',
            hotel?.name ?? '',
            propertyRoomDetails?.result[0].groups[0].rooms[0].deals[0].info.accommodation.mealPlan.name ?? '',
            propertyRoomDetails?.result[0].groups[0].rooms[0].deals[0].price.nett ?? '',
            propertyRoomDetails?.result[0].groups[0].rooms[0].deals[0].id ?? '',
            propertyRoomDetails?.result[0].hotelCode ?? '',
            propertyRoomDetails?.result[0].groups[0].rooms[0].deals[0].price.gross ?? '',
            propertyRoomDetails?.result[0].groups[0].rooms[0].deals[0].price.discount ?? '',
          );
          saveRoomAndRate(
            propertyRoomDetails?.result[0].groups[0].rooms[0] ?? '',
            propertyRoomDetails?.result[0].groups[0].rooms[0].deals[0] ?? '',
          );
        }
      } catch (error) {
        console.error('Error fetching hotel availability:', error);
      }
    };

    const loaderTimeout = setTimeout(() => {
      setShowLoader(false);
    }, delay);

    if (hotel && isSuccess) {
      // getAvailableRoomsAndPrice();
      getResponseTokenForSearch();
    }

    return () => clearTimeout(loaderTimeout);
  }, [hotel, isSuccess]);

  const hasFetchedDetails = useRef(false);

  const fetchHotelDetailsOnce = async () => {
    if (!hasFetchedDetails.current && hotel && hotel.destinationCode && hotel.destId) {
      hasFetchedDetails.current = true; // Set it to true to prevent further calls
      try {
        const data = await fetchPropertyDetails(hotel.destinationCode, hotel.destId);
        setPropertyDetails(data as PropertyDetails);
      } catch (error) {
        console.error('Error fetching hotel details:', error);
      }
    }
  };

  if (isSuccess && hotel) {
    fetchHotelDetailsOnce();
  }

  if (isLoading || showLoader) {
    return <PageLoaderComponent
      location={hotelName || "Your Destination"}
      checkIn={CheckIn}
      checkOut={CheckOut}
    />;
  }

  if (!hotel) {
    return <PageLoaderComponent
      location={hotelName}
      checkIn={CheckIn}
      checkOut={CheckOut}
    />;
  }

  const scrollToElement = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      const elementTop = targetRef.current.getBoundingClientRect().top;
      const offset = 150;

      window.scrollBy({
        top: elementTop - offset,
        behavior: 'smooth',
      });
    }
  };

  const handleRoomSelect = (room: PropertyRoom, deal: Deal) => {
    // console.log('Selected room:', room);
    // console.log('Selected rate:', deal);
    saveRoomAndRate(room, deal);
  };

  const hotelImages = hotel.imageUrls.map(url => ({ url, alt: `Image of ${hotel.name}` }));

  const faqQuestionsStrings = Array.isArray(hotel.faqQuestions)
    ? hotel.faqQuestions.map(item => typeof item === 'string' ? item : item.value)
    : [];

  const faqAnswersStrings = Array.isArray(hotel.faqAnswers)
    ? hotel.faqAnswers.map(item => typeof item === 'string' ? item : item.value)
    : [];

  const totalCount = adultCount + childCount;

  const validGrossPrice = grossPrice ? parseFloat(grossPrice) : 0;
  const validDiscountPrice = discountPrice ? parseFloat(discountPrice) : 0;

  // Additional check to ensure parsed values are numbers; set to 0 if NaN
  const safeGrossPrice = isNaN(validGrossPrice) ? 0 : validGrossPrice;
  const safeDiscountPrice = isNaN(validDiscountPrice) ? 0 : validDiscountPrice;

  // Calculate the gross price with either a 4% or 5% increase based on the discount presence
  const modifiedGrossPriceNew = safeDiscountPrice > 0 ? safeGrossPrice * 1.04 : safeGrossPrice * 1.05;

  // Calculate the discount; if discount is 0, use 1% of the new gross price as the discount
  const finalDiscountPriceNew = safeDiscountPrice > 0 ? safeDiscountPrice * 1.04 : safeGrossPrice * 0.01;

  // const roomPriceNewOne = modifiedGrossPriceNew - finalDiscountPriceNew;

  const effectiveRate = rate ?? 0.82;

  // Update the rate calculation with a default value
  const modifiedGrossPrice = modifiedGrossPriceNew * effectiveRate;
  const finalDiscountPrice = finalDiscountPriceNew * effectiveRate;
  const roomPriceNew = modifiedGrossPrice - finalDiscountPrice;

  // const parseDate = (dateStr: string) => {
  //   const suffixes = ["th", "st", "nd", "rd"];
  //   const sanitizedDateStr = suffixes.reduce((acc, suffix) => acc.replace(suffix, ''), dateStr);
  //   return new Date(sanitizedDateStr);
  // };

  // Usage inside your component or map function
  // const checkInDate = parseDate(CheckIn); // Use this function to parse the date
  // checkInDate.setDate(checkInDate.getDate() - 1);
  // const dayBeforeCheckIn = checkInDate.toLocaleDateString('en-GB', {
  //   day: '2-digit',
  //   month: '2-digit',
  //   year: 'numeric'
  // }).split('/').reverse().join('-');

  const initialRoomDisplay = 4; // Change this number to show more or fewer rooms initially

  const roomsToDisplay = showAllRooms
    ? availablePropertyData?.result[0]?.groups.flatMap(group => group.rooms) ?? []
    : availablePropertyData?.result[0]?.groups.flatMap(group => group.rooms.slice(0, initialRoomDisplay)) ?? [];

  const totalRooms = availablePropertyData?.result[0]?.groups.flatMap(group => group.rooms).length ?? 0;

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{hotel.metaTitle}</title>
          <meta name="description" content={hotel.metaDescription} />
        </Helmet>
      </HelmetProvider>

      <div className="space-y-6 md:px-0 lg:px-0 mt-12">
        <SearchSummaryBar country={hotel.country} type={hotel.type[0]} ref={searchSummaryBarRef} />
        <div className='px-3 md:px-0'>
          <Breadcrumbs />
        </div>


        {/* Mobile */}


        <div className='md:hidden lg:hidden lgx2:hidden'>
          <div className="px-3 md:px-0">
            <div className="flex">
              {Array.from({ length: hotel.starRating }).map((_, index) => (
                <AiFillStar key={index} className="text-yellow-400" />
              ))}
            </div>
            <h1 className="text-3xl font-bold">{hotel.name}</h1>
            <h4 className="flex flex-row text-sm text-gray-500 mb-4 items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className="h-3 w-3 mt-1 mr-1"
                fill="currentColor"
              >
                <path
                  d="M256 0C153.755 0 70.573 83.182 70.573 185.426c0 126.888 165.939 313.167 173.004 321.035 6.636 7.391 18.222 7.378 24.846 0 7.065-7.868 173.004-194.147 173.004-321.035C441.425 83.182 358.244 0 256 0zm0 278.719c-51.442 0-93.292-41.851-93.292-93.293S204.559 92.134 256 92.134s93.291 41.851 93.291 93.293-41.85 93.292-93.291 93.292z"
                  fill="#d90062"
                ></path>
              </svg>
              {hotel.city}, {hotel.country} -
              <span
                onClick={toggleMap}
                className="text-xs cursor-pointer text-[#116FFF] ml-1 hover:underline"
              >
                Show map
              </span>
            </h4>

            {/* Map Popup */}
            {showMap && (
              <div className="map-popup-container">
                <div className="map-popup-content">
                  <button onClick={toggleMap} className="map-popup-close-button">
                    ×
                  </button>
                  <GoogleMapComponent
                    apiKey="AIzaSyD3BQK4fqJwhNVoFZyk6EB1whUD-3NehqI"
                    latitude={parseFloat(hotel.latitude)}
                    longitude={parseFloat(hotel.longitude)}
                    zoom={17}
                  />
                </div>
              </div>
            )}
          </div>
          <div className='md:flex'>
            <div className="flex flex-col md:flex-row lg:flex-row gap-4 md:w-[800px] 2xl:w-[1000px]">
              {featuredImage && <HotelImageGallery images={hotelImages} featuredImage={featuredImage} setFeaturedImage={setFeaturedImage} />}
            </div>
            <div className='p-4'>
              {hotel.dealHighlights && hotel.dealHighlights.length > 0 && (
                <WhyThisResort hotel={hotel} />
              )}
            </div>

            <HotelDetails
              hotelId={hotel.destId}
              slug={hotel.slug}
              roomDetails={hotel.description}
              facilitiesTitle={`Facilities at ${slug}`}
              mapTitle={`Map Location for ${hotel.name}`}
              latitude={hotel.latitude?.toString() || ''}
              longitude={hotel.longitude?.toString() || ''}
              tourQuery={hotel.country}
              destinationCode={hotel.destinationCode}
              hasWhyThisResort={hasWhyThisResort(hotel)}
            />
            <div className='p-4 -mt-10'>
              <TripAdvisorRating hotelName={slug} />
            </div>

            <div className='mt-4 md:w-[800px] 2xl:w-[1000px]' id='target'>
              <span className='text-2xl font-bold mx-4 md:mx-0'>Room Types</span>
              {availablePropertyData?.result && availablePropertyData.result.length > 0 ? (
                availablePropertyData.result.every(hotel => hotel.groups[0].rooms.length === 0) ? (
                  <p className="text-red-700">No rooms are available for the selected dates.</p>
                ) : (
                  <>
                    {roomsToDisplay.map((room, index) => (
                      <RoomsAndPrice
                        rate={effectiveRate}
                        key={`${index}-${room.code}`}
                        room={room}
                        hotelName={hotelName}
                        totalCount={totalCount}
                        slug={slug}
                        hotelId={hotelId}
                        availabilityData={availablePropertyData}
                        onSelect={handleRoomSelect}
                      />
                    ))}
                    {totalRooms > initialRoomDisplay && (
                      <div className="text-center mt-4 mb-4">
                        <button
                          className="group relative inline-flex items-center justify-center text-sm font-medium tracking-wide text-[#0050b8] hover:text-blue-700 transition-all duration-200 ease-in-out underline"
                          onClick={handleToggleShowAllRooms}
                        >
                          <span className="mr-2">
                            {showAllRooms ? 'Show Less Rooms' : 'View More Rooms'}
                          </span>
                          <svg
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${showAllRooms ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )
              ) : (
                <>
                  <div className="container mx-auto p-4 flex justify-center items-center h-[24rem]">
                    <div className="p-4 shadow-md rounded-lg bg-white border-t border-gray-200 justify-between flex flex-col items-center">
                      <div className="flex items-center">
                        <img src="/featureIcons/no-avail.svg" alt="Icon" className="w-40 h-40" width="40" height="40" />
                      </div>
                      <div className="flex flex-col justify-center text-center">
                        <h4 className="text-base font-semibold">We have no rooms available at this property for your requested dates.</h4>
                        <p className="text-sm text-halo-pink mt-2">Don't give up yet – adjust your dates or explore a variety of other incredible hotel options waiting for you.</p>
                        <div className='flex items-center gap-2 mt-4'>
                          <button
                            type="button"
                            onClick={handleChangeDatesClick}
                            className="bg-halo-pink text-white h-full py-2 px-4 font-medium hover:bg-pink-800 text-base w-full rounded-lg"
                          >
                            Change dates
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className='flex-[1_1_0%] md:ml-8 px-4 md:px-0 mb-6 mt-6 md:mt-0' id='inquire'>
              <div className="p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border border-gray-300 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base font-medium text-gray-900">Check-in</div>
                    <div className="text-sm font-normal text-[#0050b8]">{CheckIn}</div>
                  </div>
                  <div className="benefits-divider w-1/12 bg-gray-300 min-h-full"></div>
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base font-medium text-gray-900">Check-out</div>
                    <div className="text-sm font-normal text-[#0050b8]">{CheckOut}</div>
                  </div>
                </div>
                <hr className="mb-2" />
                <div className="flex justify-center items-center mb-2 text-md text-gray-800">
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">{nights}</span>
                    <span className="text-sm">{nights === 1 ? ' Night' : ' Nights'}</span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">{adultCount}</span>
                    <span className="text-sm">{adultCount > 1 ? ' Adults' : ' Adult'}</span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">{childCount}</span>
                    <span className="text-sm">{childCount === 1 ? ' Child' : ' Children'}</span>
                  </span>
                </div>
                <hr className="mb-2" />
                <div className="text-base font-medium text-gray-900">
                  Room Name:<br />
                  <span
                    className={`font-normal text-sm ${roomName === 'Not available for Selected Dates' ? 'text-red-500' : 'text-gray-700'
                      }`}
                  >
                    {roomName}
                    {roomName !== 'Not available for Selected Dates' && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Room)
                      </a>
                    )}
                  </span>
                </div>
                <div className="text-base font-medium text-gray-900 mt-2 mb-6">
                  Meal Plan:<br />
                  <span className="text-gray-700 font-normal text-sm">
                    {roomType && roomType !== ' - ' ? roomType.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()) : roomType}
                    {roomType && roomType !== ' - ' && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Plan)
                      </a>
                    )}
                  </span>
                </div>
                <div className="mb-2 text-sm font-semibold text-gray-800 text-right px-2 flex justify-between items-center">
                  <span className='text-right w-full'>Price:</span>
                  <span className="text-gray-700 font-normal line-through ml-1 min-w-20 inline-block">£ {(modifiedGrossPrice / totalCount).toFixed(2)}</span>
                </div>
                <div className="flex justify-end">
                  <div className="flex items-center bg-[#f7fffa] px-2 py-1 mb-2 rounded-md min-w-max text-right">
                    {/* <img src="/htag.svg" alt="Save Icon" className="w-4 h-4 mr-1" /> */}
                    <span className="text-sm font-medium text-gray-700">You save:</span>
                    <span className="text-green-600 font-medium text-sm ml-1 min-w-20 inline-block">
                      - £ {(finalDiscountPrice / totalCount).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="text-right px-2 flex justify-end items-center">
                  <span className='text-lg font-semibold text-gray-700 mb-0 w-full'>Final price:</span>
                  <span className="text-xl font-normal text-[#0050b8] min-w-[110px] inline-block text-right">£ {(roomPriceNew / totalCount).toFixed(2)}</span>
                </div>
                <div className="text-right px-2 -mt-2">
                  <span className="text-xs font-normal text-[#0050b8]">Per person</span>
                </div>
                <div className="text-right px-2 -mt-1">
                  <span className="text-xs font-normal text-gray-400">Inc. taxes & charges</span>
                </div>
                <hr className="mb-2 mt-4" />
                <div className="flex justify-between items-center mt-4">
                  <span className="relative flex items-center">
                    <div className="text-xs text-[#0050b8]">
                      What if I cancel?
                    </div>
                    <span className="relative inline-flex ml-1 group">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        className="bi bi-info-circle cursor-pointer text-gray-500"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-[250px] mb-2 hidden group-hover:block p-3 bg-gray-800 text-white text-xs rounded-lg shadow-md text-center">
                        {availablePropertyData &&
                          availablePropertyData.result.length > 0 &&
                          availablePropertyData.result[0].groups[0].rooms[0].deals[0].cancellationPolicies.length > 0 ? (
                          <span className="block mb-2">
                            <p className="text-white text-xs">
                              {new Date(availablePropertyData.result[0].groups[0].rooms[0].deals[0].cancellationPolicies[0].date) < new Date()
                                ? 'Non-refundable'
                                : `Free Cancellation upto ${new Date(availablePropertyData.result[0].groups[0].rooms[0].deals[0].cancellationPolicies[0].date)
                                  .toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}`}
                            </p>
                          </span>
                        ) : (
                          <span>Free Cancellation</span>
                        )}
                      </div>
                    </span>
                  </span>

                  {/* <div className="text-right px-2">
                    <span className="text-base font-semibold text-gray-700">Total Price:</span>
                    <span className="text-sm font-normal text-halo-pink ml-2">$ {roomPriceNew.toFixed(2)}</span>
                  </div> */}
                </div>


                <GuestInfoForm
                  price={roomPriceNew.toFixed(2)}
                  pricePerNight={hotel.pricePerNight}
                  hotelId={hotel._id}
                  packageType={hotel.packageType}
                  depositPrice={hotel.depositPrice?.toString() || ''}
                  destId={hotel.destId}
                  hotelName={hotel.name}
                  slug={hotel.slug}
                  propertyToken={hotel.serpId}
                  roomsAndBoard={hotel.roomsAndBoard}
                  cancellation={hotel.cancellation}
                  onSelect={handleRoomSelect}
                  responseToken={availablePropertyData?.responseToken ?? ''}
                  hotel={hotel}
                  date={CheckIn}
                />
              </div>
              <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6 flex items-center justify-between h-[117px]">
                <div className="icon flex items-center mr-4">
                  <img src="/featureIcons/quality.svg" alt="Icon" className="w-10 h-10" width="40" height="40" />
                </div>
                <div className="content flex flex-col justify-center mx-4 flex-grow">
                  <h4 className="text-lg font-semibold">Price Match Guarantee</h4>
                  <p className="text-sm text-gray-800">We are not beaten on price</p>
                  <Link
                    to="/halo-promise#promise5"
                    className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See how it works
                  </Link>
                </div>
              </div>
              <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6 flex items-center justify-between h-[117px]">
                <div className="icon flex items-center mr-4">
                  <img src="/featureIcons/calen.svg" alt="Icon" className="w-10 h-10" width="40" height="40" />
                </div>
                <div className="content flex flex-col justify-center mx-4 flex-grow">
                  <h4 className="text-lg font-semibold">Easy Payment Plan</h4>
                  <p className="text-sm text-gray-800">Convenient payment arrangements for you</p>
                  <Link
                    to="/halo-promise#promise2"
                    className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See how it works
                  </Link>
                </div>
              </div>

            </div>
          </div>

          {propertyDetails?.panorama != null && (
            <>
              <h2 className='mt-16 mx-4 md:mx-0 text-2xl'>Want to look around?</h2>
              <p className="text-gray-700 md:mb-2 md:mt-2">
                Enjoy a 360° virtual tour of your resort from the comfort of your home.
              </p>
              <div className='p-4 md:p-0'>
                <iframe
                  src={propertyDetails?.panorama}
                  width="100%"
                  height="400px" // Set the default height for mobile
                  className="md:h-[600px]" // Adjust the height for larger screens
                  allowFullScreen
                  title="360 Hotel View"
                />
              </div>
            </>
          )}

          <div className='p-4'>
            <ContactDetailCard />
          </div>
          {/* <h2 className="mt-16 mx-4 md:mx-0">What's the weather in <span className="text-halo-pink">{hotel.country}</span> like?</h2>
          <WeatherComponent startDate={CheckInDate} latitude={parseFloat(hotel.latitude)} longitude={parseFloat(hotel.longitude)} /> */}
          <FAQsComponent
            faqQuestions={faqQuestionsStrings}
            faqAnswers={faqAnswersStrings}
          />
          <h2 className='mt-8 mb-2 mx-4 md:mx-0'>You Might Also Like</h2>
          <SimilarHotelsList hotels={hotels ?? []} hotelType={hotel.type[0]} hotelName={hotel.slug} />

          <h2 className="mt-8 mb-2 mx-4 md:mx-0">Explore, Dream, Discover: Travel Stories & Guides</h2>
          <div className='p-4 md:p-0'>
            <BlogSliderHome />
          </div>
        </div>


        {/* Desktop */}


        <div className='hidden md:block lg:block lgx2:block'>
          <div className="px-3 md:px-0">
            <div className="flex">
              {Array.from({ length: hotel.starRating }).map((_, index) => (
                <AiFillStar key={index} className="text-yellow-400" />
              ))}
            </div>
            <h1 className="text-3xl font-bold">{hotel.name}</h1>
            <h4 className="flex flex-row text-sm text-gray-500 mb-4 items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className="h-3 w-3 mt-1 mr-1"
                fill="currentColor"
              >
                <path
                  d="M256 0C153.755 0 70.573 83.182 70.573 185.426c0 126.888 165.939 313.167 173.004 321.035 6.636 7.391 18.222 7.378 24.846 0 7.065-7.868 173.004-194.147 173.004-321.035C441.425 83.182 358.244 0 256 0zm0 278.719c-51.442 0-93.292-41.851-93.292-93.293S204.559 92.134 256 92.134s93.291 41.851 93.291 93.293-41.85 93.292-93.291 93.292z"
                  fill="#d90062"
                ></path>
              </svg>
              {hotel.city}, {hotel.country} -
              <span
                onClick={toggleMap}
                className="text-xs cursor-pointer text-[#0050b8] ml-1 hover:underline"
              >
                Show map
              </span>
            </h4>

            {/* Map Popup */}
            {showMap && (
              <div className="map-popup-container show">
                <div className="map-popup-content">
                  <button onClick={toggleMap} className="map-popup-close-button">
                    ×
                  </button>
                  <GoogleMapComponent
                    apiKey="AIzaSyD3BQK4fqJwhNVoFZyk6EB1whUD-3NehqI"
                    latitude={parseFloat(hotel.latitude)}
                    longitude={parseFloat(hotel.longitude)}
                    zoom={17}
                  />
                </div>
              </div>
            )}
          </div>
          <div className='flex'>
            <div className="flex flex-col md:flex-row lg:flex-row gap-4 mx-auto md:w-[67.7%] 2xl:w-[69.5%] pr-6">
              {featuredImage && <HotelImageGallery images={hotelImages} featuredImage={featuredImage} setFeaturedImage={setFeaturedImage} />}
            </div>

            <div className='flex-[1_1_0%] px-4 md:px-0 mt-6 md:mt-0'>
              <div className='mb-6 mx-auto'>
                <TripAdvisorRating hotelName={slug} />
              </div>
              <div>
                {hotel.dealHighlights && hotel.dealHighlights.length > 0 && (
                  <WhyThisResort hotel={hotel} />
                )}
              </div>
            </div>
          </div>

          <HotelDetails
            hotelId={hotel.destId}
            slug={hotel.slug}
            roomDetails={hotel.description}
            facilitiesTitle={`Facilities at ${slug}`}
            mapTitle={`Map Location for ${hotel.name}`}
            latitude={hotel.latitude?.toString() || ''}
            longitude={hotel.longitude?.toString() || ''}
            tourQuery={hotel.country}
            destinationCode={hotel.destinationCode}
            hasWhyThisResort={hasWhyThisResort(hotel)}
          />

          <div className='flex mt-12'>
            <div className='md:w-[800px] 2xl:w-[1000px] mr-6' id='inquire2'>
              <span className='text-2xl font-bold'>Room Types</span>
              {availablePropertyData?.result && availablePropertyData.result.length > 0 ? (
                availablePropertyData.result.every(hotel => hotel.groups[0].rooms.length === 0) ? (
                  <p className="text-red-700">No rooms are available for the selected dates.</p>
                ) : (
                  <>
                    {roomsToDisplay.map((room, index) => (
                      <RoomsAndPrice
                        key={`${index}-${room.code}`}
                        rate={effectiveRate}
                        room={room}
                        hotelName={hotelName}
                        totalCount={totalCount}
                        slug={slug}
                        hotelId={hotelId}
                        availabilityData={availablePropertyData}
                        onSelect={handleRoomSelect}
                      />
                    ))}
                    {totalRooms > initialRoomDisplay && (
                      <div className="text-center mt-4">
                        <button
                          className="group relative inline-flex items-center justify-center text-sm font-medium tracking-wide text-[#0050b8] hover:text-blue-700 transition-all duration-200 ease-in-out underline"
                          onClick={handleToggleShowAllRooms}
                        >
                          <span className="mr-2">
                            {showAllRooms ? 'Show Less Rooms' : 'View More Rooms'}
                          </span>
                          <svg
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${showAllRooms ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )
              ) : (
                <>
                  <div className="container p-6 shadow-md rounded-lg bg-white mx-auto border-t border-gray-200 mt-4 flex items-center justify-between h-[15REM]">
                    <div className="icon flex items-center mr-4 ml-4">
                      <img src="/featureIcons/no-avail.svg" alt="Icon" className="w-40 h-40" width="40" height="40" />
                    </div>
                    <div className="content flex flex-col justify-center mx-4 flex-grow">
                      <h4 className="text-lg font-semibold">We have no rooms available at this property for your requested dates.</h4>
                      <p className="text-sm text-halo-pink">Don't give up yet – adjust your dates or explore a variety of other incredible hotel options waiting for you.</p>
                      <div className='flex items-center gap-2 mt-4'>
                        <button
                          type="button"
                          onClick={handleChangeDatesClick}
                          className="bg-halo-pink text-white h-full py-2 px-4 font-medium hover:bg-pink-800 text-md w-[30%] rounded-lg"
                        >
                          Change dates
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {propertyDetails?.panorama != null && (
                <>
                  <h2 className='mt-16 mx-4 md:mx-0 text-2xl'>Want to look around?</h2>
                  <p className="text-gray-700 md:mb-2 md:mt-2">
                    Enjoy a 360° virtual tour of your resort from the comfort of your home.
                  </p>
                  <div className='p-4 md:p-0'>
                    <iframe
                      src={propertyDetails?.panorama}
                      width="100%"
                      height="400px" // Set the default height for mobile
                      className="md:h-[600px] md:w-[800px] 2xl:w-[1000px]" // Adjust the height for larger screens
                      allowFullScreen
                      title="360 Hotel View"
                    />
                  </div>
                </>
              )}
              {/* <h2 className="mt-16 mx-4 md:mx-0">What's the weather in <span className="text-halo-pink">{hotel.country}</span> like?</h2>
          <WeatherComponent startDate={CheckInDate} latitude={parseFloat(hotel.latitude)} longitude={parseFloat(hotel.longitude)} /> */}
              <FAQsComponent
                faqQuestions={faqQuestionsStrings}
                faqAnswers={faqAnswersStrings}
              />
            </div>
            <div className='flex-[1_1_0%] mt-12'>
              <div className="p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base font-medium text-gray-900">Check-in</div>
                    <div className="text-sm font-normal text-[#0050b8]">{CheckIn}</div>
                  </div>
                  <div className="benefits-divider w-1/12 bg-gray-300 min-h-full"></div>
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base font-medium text-gray-900">Check-out</div>
                    <div className="text-sm font-normal text-[#0050b8]">{CheckOut}</div>
                  </div>
                </div>
                <hr className="mb-2" />
                <div className="flex justify-center items-center mb-2 text-base text-gray-800">
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">{nights}</span>
                    <span className="text-sm">{nights === 1 ? ' Night' : ' Nights'}</span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">{adultCount}</span>
                    <span className="text-sm">{adultCount > 1 ? ' Adults' : ' Adult'}</span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">{childCount}</span>
                    <span className="text-sm">{childCount === 1 ? ' Child' : ' Children'}</span>
                  </span>
                </div>
                <hr className="mb-4" />
                <div className="text-base font-medium text-gray-900">
                  Room Name:<br />
                  <span
                    className={`font-normal text-sm ${roomName === 'Not available for Selected Dates' ? 'text-red-500' : 'text-gray-700'
                      }`}
                  >
                    {roomName}
                    {roomName !== 'Not available for Selected Dates' && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Room)
                      </a>
                    )}
                  </span>
                </div>
                <div className="text-base font-medium text-gray-900 mt-2 mb-6">
                  Meal Plan:<br />
                  <span className="text-gray-700 font-normal text-sm">
                    {roomType && roomType !== ' - ' ? roomType.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()) : roomType}
                    {roomType && roomType !== ' - ' && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Plan)
                      </a>
                    )}
                  </span>
                </div>
                <div className="flex justify-end">
                  <div className="text-sm font-medium text-gray-800 text-right px-2 flex justify-between items-center">
                    <span className="text-right">Price:</span>
                    <span className="text-gray-700 font-normal line-through ml-1" style={{ minWidth: '90px', display: 'inline-block', textAlign: 'right' }}>
                      £ {(modifiedGrossPrice / totalCount).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="flex items-center bg-[#f7fffa] px-2 py-1 rounded-md min-w-max text-right">
                    {/* Removed the image for clarity in the example. Uncomment or modify as needed. */}
                    {/* <img src="/htag.svg" alt="Save Icon" className="w-4 h-4 mr-1" /> */}
                    <span className="text-sm font-medium text-gray-700">You save:</span>
                    <span className="text-green-600 font-medium text-sm ml-1" style={{ minWidth: '90px', display: 'inline-block', textAlign: 'right' }}>
                      - £ {(finalDiscountPrice / totalCount).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="text-right px-2 flex justify-end">
                  <div className="text-right">
                    <span className='text-sm font-medium text-gray-700 mb-0'>Final price</span>
                    <span className="relative inline-flex items-center ml-1 group">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        fill="currentColor"
                        className="bi bi-info-circle cursor-pointer text-gray-500"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-[250px] mb-2 hidden group-hover:block p-3 bg-gray-800 text-white text-xs rounded-lg shadow-md text-center">
                        <span className="text-white">Inc. taxes & charges</span>
                      </div>
                    </span>
                  </div>
                  <span className="text-xl font-medium text-[#0050b8]" style={{ minWidth: '110px', display: 'inline-block', textAlign: 'right' }}>
                    £ {(roomPriceNew / totalCount).toFixed(2)}
                  </span>
                </div>
                <div className="text-right px-2 -mt-2">
                  <span className="text-xs font-normal text-[#0050b8]">Per person</span>
                </div>
                <hr className="mb-2 mt-4" />
                <div className="flex justify-between items-center mt-4">
                  <span className="relative flex items-center">
                    <div className="text-xs text-[#0050b8]">
                      What if I cancel?
                    </div>
                    <span className="relative inline-flex ml-1 group">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        className="bi bi-info-circle cursor-pointer text-gray-500"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-[250px] mb-2 hidden group-hover:block p-3 bg-gray-800 text-white text-xs rounded-lg shadow-md text-center">
                        {availablePropertyData &&
                          availablePropertyData.result.length > 0 &&
                          availablePropertyData.result[0].groups[0].rooms[0].deals[0].cancellationPolicies.length > 0 ? (
                          <span className="block mb-2">
                            <p className="text-white text-xs">
                              {new Date(availablePropertyData.result[0].groups[0].rooms[0].deals[0].cancellationPolicies[0].date) < new Date()
                                ? 'Non-refundable'
                                : `Free Cancellation upto ${new Date(availablePropertyData.result[0].groups[0].rooms[0].deals[0].cancellationPolicies[0].date)
                                  .toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}`}
                            </p>
                          </span>
                        ) : (
                          <span>Free Cancellation</span>
                        )}
                      </div>
                    </span>
                  </span>
                </div>


                <GuestInfoForm
                  price={roomPriceNew.toFixed(2)}
                  pricePerNight={hotel.pricePerNight}
                  hotelId={hotel._id}
                  packageType={hotel.packageType}
                  depositPrice={hotel.depositPrice?.toString() || ''}
                  destId={hotel.destId}
                  hotelName={hotel.name}
                  slug={hotel.slug}
                  propertyToken={hotel.serpId}
                  roomsAndBoard={hotel.roomsAndBoard}
                  cancellation={hotel.cancellation}
                  onSelect={handleRoomSelect}
                  responseToken={availablePropertyData?.responseToken ?? ''}
                  hotel={hotel}
                  date={CheckIn}
                />
              </div>

              <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6 flex items-center justify-between h-[100px]">
                <div className="icon flex items-center mr-4">
                  <img src="/featureIcons/quality.svg" alt="Icon" className="w-10 h-10" width="40" height="40" />
                </div>
                <div className="content flex flex-col justify-center mx-4 flex-grow">
                  <h4 className="text-lg font-semibold">Price Match Guarantee</h4>
                  <p className="text-sm text-gray-800">We are not beaten on price</p>
                  <Link
                    to="/halo-promise#promise5"
                    className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See how it works
                  </Link>
                </div>
              </div>
              <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 flex items-center justify-between h-[100px]">
                <div className="icon flex items-center mr-4">
                  <img src="/featureIcons/calen.svg" alt="Icon" className="w-10 h-10" width="40" height="40" />
                </div>
                <div className="content flex flex-col justify-center mx-4 flex-grow">
                  <h4 className="text-lg font-semibold">Easy Payment Plan</h4>
                  <p className="text-sm text-gray-800">Convenient payment arrangements for you</p>
                  <Link
                    to="/halo-promise#promise2"
                    className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See how it works
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 space-y-8 px-4 md:px-0">
            <ContactDetailCard />

            <div>
              <h2 className='mb-2'>You Might Also Like</h2>
              <SimilarHotelsList hotels={hotels ?? []} hotelType={hotel.type[0]} hotelName={hotel.slug} />
            </div>

            <div>
              <h2 className="mb-2">Explore, Dream, Discover: Travel Stories & Guides</h2>
              <BlogSliderHome />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Detail;
